import { Dialog } from "@/Dialog";
import SearchBox from "@/common/SearchBox";
import { LayoutHeaderInButton } from "@/HeaderInButton";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useBusinessCompanyMember,
  useJoinBusinessCompanyMember,
} from "~/services/business/myPage";
import { formatPhoneNumber } from "../../../utils/dataTransform";

export const Route = createFileRoute(
  "/business/layout/user-employee-management"
)({
  component: UserSettingEmployeeManagement,
});

function UserSettingEmployeeManagement() {
  const { history } = useRouter();
  const queryClient = useQueryClient();
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");

  const {
    data: companyMemberData,
    isLoading,
    error,
  } = useBusinessCompanyMember();

  const joinMemberMutation = useJoinBusinessCompanyMember();

  // 검색 기능
  const filteredMembers = useMemo(() => {
    if (!companyMemberData?.infoBusinessUserDtoList) return [];

    if (!searchKeyword.trim()) {
      return companyMemberData.infoBusinessUserDtoList;
    }

    return companyMemberData.infoBusinessUserDtoList.filter((member) =>
      member.bizUserName.toLowerCase().includes(searchKeyword.toLowerCase())
    );
  }, [companyMemberData?.infoBusinessUserDtoList, searchKeyword]);

  const handleVisible = () => {
    setVisible(true);
    setEmail("");
    setErrorMessage("");
  };

  const handleInvisible = () => {
    setVisible(false);
    setEmail("");
    setErrorMessage("");
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleConfirm = async () => {
    if (!email.trim()) {
      setErrorMessage("이메일을 입력해주세요");
      return;
    }

    if (!validateEmail(email)) {
      setErrorMessage("올바르지 않은 이메일입니다");
      return;
    }

    try {
      const result = await joinMemberMutation.mutateAsync({ email });
      console.log(result);
      if (result.statusCode === 200) {
        setErrorMessage("");
        // 성공 시 추가 처리 (예: 토스트 메시지 등)
        console.log("임직원 초대 성공:", result.message);
        // 임직원 목록 새로고침
        queryClient.invalidateQueries({
          queryKey: ["business", "mypage", "company-member"],
        });
        // 성공 시 팝업 닫기
        handleInvisible();
      } else {
        setErrorMessage(result.message || "임직원 초대 중 오류가 발생했습니다");
      }
    } catch (error) {
      console.error("임직원 초대 에러:", error);
      setErrorMessage("임직원 초대 중 오류가 발생했습니다");
    }
  };

  const handleSearch = (keyword: string) => {
    setSearchKeyword(keyword);
  };
  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}.${month}.${day}`;
    } catch {
      return dateString;
    }
  };

  if (error) {
    return (
      <>
        <LayoutHeaderInButton
          title={"임직원 정보 관리"}
          hasBackBtn
          hasFootPrint={false}
        >
          <button
            className="text-sm text-gray-400 text-[#666666]"
            onClick={handleVisible}
          >
            + 추가
          </button>
        </LayoutHeaderInButton>
        <div className="flex flex-col flex-auto bg-[#F5F5F5] justify-center items-center">
          <div className="text-red-500">
            임직원 목록을 불러오는 중 오류가 발생했습니다.
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <LayoutHeaderInButton
        title={"임직원 정보 관리"}
        hasBackBtn
        hasFootPrint={false}
      >
        <button
          className="text-sm text-gray-400 text-[#666666]"
          onClick={handleVisible}
        >
          + 추가
        </button>
      </LayoutHeaderInButton>
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col bg-[#F5F5F5]">
        <div className="px-4 py-3">
          <SearchBox onSearch={handleSearch} placeholder="이름으로 검색" />
        </div>

        {/* 임직원 리스트 */}
        <div className="overflow-hidden mx-4 rounded-lg">
          {/* 헤더 */}
          <div className="grid grid-cols-[20%_40%_40%] px-4 py-1 bg-gray-50 gap-2 mb-1">
            <div className="text-sm font-medium text-center text-[#666666]">
              이름
            </div>
            <div className="text-sm font-medium text-center text-[#666666]">
              연락처
            </div>
            <div className="text-sm font-medium text-center text-[#666666]">
              등록일
            </div>
          </div>

          {/* 로딩 상태 */}
          {isLoading && (
            <div className="flex justify-center items-center py-8">
              <div className="text-sm text-gray-500">로딩 중...</div>
            </div>
          )}

          {/* 리스트 아이템들 */}
          {!isLoading && (
            <div>
              {filteredMembers.length > 0 ? (
                filteredMembers.map((member) => (
                  <div
                    key={member.bizUserSeq}
                    className="grid grid-cols-[20%_40%_40%] px-4 py-4 bg-white transition-colors gap-2 mb-2 border-[1px] border-[#E9E9E9] rounded-lg shadow-sm"
                    onClick={() =>
                      history.push(
                        `/business/layout/user-employee-detail?id=${member.bizUserSeq}`
                      )
                    }
                  >
                    <div className="col-span-1 text-sm font-medium text-center text-gray-900">
                      {member.bizUserName}
                    </div>
                    <div className="col-span-1 text-sm text-center text-gray-600">
                      {formatPhoneNumber(member.phoneNumber)}
                    </div>
                    <div className="col-span-1 text-sm text-center text-gray-600">
                      {formatDate(member.dtRegist)}
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex justify-center items-center py-8">
                  <div className="text-sm text-gray-500">
                    {searchKeyword
                      ? "검색 결과가 없습니다."
                      : "등록된 임직원이 없습니다."}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <Dialog visible={visible} onClose={handleInvisible}>
        <div className="flex flex-col gap-4">
          <div className="text-lg font-semibold text-black">
            임직원 정보 등록
          </div>
          <div className="text-sm text-[#666666]">
            임직원의 가입 아이디(이메일)을 입력하세요
          </div>

          <div className="flex flex-col">
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errorMessage) setErrorMessage("");
              }}
              placeholder="아이디(이메일)을 입력하세요"
              className="w-full px-4 py-3 border border-[#E9E9E9] rounded-lg text-sm focus:outline-none focus:border-[#FF8A00]"
            />
            {errorMessage && (
              <div className="mt-2 text-sm text-red">{errorMessage}</div>
            )}
          </div>

          <div className="flex gap-3 mt-4">
            <button
              onClick={handleInvisible}
              className="flex-1 py-3 border border-[#E9E9E9] rounded-lg text-sm text-gray-600 hover:bg-gray-50"
            >
              취소
            </button>
            <button
              onClick={handleConfirm}
              disabled={joinMemberMutation.isPending}
              className="flex-1 bg-[#FF8A00] text-white py-3 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#E67A00]"
            >
              {joinMemberMutation.isPending ? "전송 중..." : "확인"}
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
